import type { BundleImportItem } from '@/lib/api/generated/model';

import type { AnswersRequest } from './answers';

/**
 * What the person said about each bundle item whose key the workspace already
 * holds (spec 204; service spec 163).
 *
 * Pure, so the rules — what a complete set of answers is, what the service is
 * sent, when the dialog has to check again — are testable without a dialog.
 */
export type ItemChoice =
  | { action: 'use_existing' }
  | { action: 'rename'; newKey: string }
  | { action: 'replace' }
  | { action: 'update' };

/** Keyed by {@link itemId}: a workflow and an agent may share a key. */
export type BundleChoices = Record<string, ItemChoice>;

export function itemId(item: Pick<BundleImportItem, 'kind' | 'key'>): string {
  return `${item.kind}:${item.key}`;
}

/** The items the person has to (or already did) decide about. */
export function decidable(
  items: readonly BundleImportItem[],
): BundleImportItem[] {
  return items.filter(
    (item) => item.state === 'conflict' || item.answer != null,
  );
}

/** Every item still waiting for a choice the person has not made here. */
export function unanswered(
  items: readonly BundleImportItem[],
  choices: BundleChoices,
): BundleImportItem[] {
  return items.filter((item) => {
    if (item.state !== 'conflict') return false;
    const choice = choices[itemId(item)];
    if (!choice) return true;
    return choice.action === 'rename' && choice.newKey.trim() === '';
  });
}

/** The service's `BundleImportAnswers`, as the JSON form field carries it. */
export interface BundleAnswersRequest {
  workflows?: Record<
    string,
    { action: 'use_existing' | 'rename' | 'update'; new_key?: string }
  >;
  agents?: Record<string, 'use_existing' | 'replace'>;
  skills?: Record<string, 'use_existing' | 'replace'>;
  substitutions?: AnswersRequest;
}

export function toBundleAnswers(
  choices: BundleChoices,
  substitutions: AnswersRequest | undefined,
): BundleAnswersRequest {
  const request: BundleAnswersRequest = {};
  for (const [id, choice] of Object.entries(choices)) {
    const [kind, ...rest] = id.split(':');
    const key = rest.join(':');
    if (kind === 'workflow') {
      if (choice.action === 'replace') continue;
      request.workflows ??= {};
      request.workflows[key] =
        choice.action === 'rename'
          ? { action: 'rename', new_key: choice.newKey.trim() }
          : { action: choice.action };
    } else if (kind === 'agent' || kind === 'skill') {
      if (choice.action === 'rename' || choice.action === 'update') continue;
      const group = kind === 'agent' ? 'agents' : 'skills';
      request[group] ??= {};
      request[group][key] = choice.action;
    }
  }
  if (substitutions) request.substitutions = substitutions;
  return request;
}

/** Whether two sets of answers would be sent identically: "Check again" when not. */
export function sameAnswers(
  a: BundleAnswersRequest,
  b: BundleAnswersRequest,
): boolean {
  return canonical(a) === canonical(b);
}

function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as Record<string, unknown>).sort(([x], [y]) =>
            x.localeCompare(y),
          ),
        )
      : inner,
  );
}
